// @ts-check
// "Novo modelo" (spec/email.md seção 19.1): nome e quatro caminhos: "Modelos prontos" (a galeria com miniaturas, com o
// logo e a cor da escola), "Blocos" (arrastar e soltar), "Texto simples" (como um e-mail do Outlook) e "Importar HTML"
// (colar o código ou enviar um arquivo .html). Cria o modelo no servidor e devolve para a tela abrir o editor.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { createEmailTemplate, listEmailGallery } from '../../data/email-templates.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { EmailDialog } from './email-dialog.js';
import { EmailContentThumbnail } from './email-thumbnail.js';
import { HtmlImportPanel } from './html-mode.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/email-templates.js').EmailGalleryItem} EmailGalleryItem
 * @typedef {import('../../data/email-templates.js').EmailTemplate} EmailTemplate
 */

/** @param {EmailGalleryItem & { name?: string, description?: string }} item */
export const galleryItemName = (item) => (hasTranslation(`emailTemplates.gallery.items.${item.key}.name`) ? t(`emailTemplates.gallery.items.${item.key}.name`) : item.name ?? item.key);
/** @param {EmailGalleryItem & { name?: string, description?: string }} item */
export const galleryItemDescription = (item) => (hasTranslation(`emailTemplates.gallery.items.${item.key}.description`)
  ? t(`emailTemplates.gallery.items.${item.key}.description`)
  : item.description ?? '');

/**
 * Grade da galeria.
 * @param {{ onUse: (item: EmailGalleryItem) => void, busyKey?: string | null }} props
 */
export function EmailGalleryGrid({ onUse, busyKey = null }) {
  const [items, setItems] = useState(/** @type {EmailGalleryItem[] | null} */ (null));
  const [failed, setFailed] = useState(false);
  const canCreateService = hasCurrentPermissionFlag('WORKSPACE');
  const load = () => {
    setFailed(false);
    listEmailGallery().then(setItems).catch(() => setFailed(true));
  };
  useEffect(load, []);
  if (failed) {
    return html`<div class="email-inline-error" role="alert">
      <span>${t('emailTemplates.loadError')}</span>
      <${Button} title=${t('emailTemplates.retry')} size="small" variant="secondary" onClick=${load} />
    </div>`;
  }
  if (!items) return html`<div class="email-gallery-grid">${Array.from({ length: 6 }, (_, index) => html`<${Skeleton} key=${index} height=${320} radius=${8} />`)}</div>`;
  return html`<ul class="email-gallery-grid" data-testid="email-gallery">
    ${items.map((item) => html`<li key=${item.key} class="email-gallery-card">
      <${EmailContentThumbnail} title=${galleryItemName(item)}
        content=${{ document: item.document, subject: item.subject, preheader: item.preheader, purpose: item.purpose }} />
      <div class="email-gallery-texts">
        <span class="email-gallery-name">${galleryItemName(item)}</span>
        <span class="email-gallery-description">${galleryItemDescription(item)}</span>
        <span class="email-chip-row">
          <span class="email-chip">${t(`emailTemplates.categories.${item.category}`)}</span>
          <span class="email-chip">${t(`emailTemplates.modes.${item.editorMode}`)}</span>
          ${item.purpose === 'SERVICE' ? html`<span class="email-chip" data-tone="blue">${t('emailTemplates.card.service')}</span>` : null}
        </span>
        ${item.purpose === 'SERVICE' && !canCreateService ? html`<span class="input-hint">${t('emailTemplates.purposes.serviceNeedsWorkspace')}</span>` : null}
      </div>
      <${Button} title=${t('emailTemplates.gallery.use')} size="small" accent="blue" isLoading=${busyKey === item.key} disabled=${Boolean(busyKey)}
        onClick=${() => onUse(item)} />
    </li>`)}
  </ul>`;
}

/**
 * @param {{ onClose: () => void, onCreated: (template: EmailTemplate) => void, initialStep?: 'CHOOSE' | 'GALLERY' }} props
 */
export function CreateEmailTemplateDialog({ onClose, onCreated, initialStep = 'CHOOSE' }) {
  const [step, setStep] = useState(/** @type {'CHOOSE' | 'GALLERY' | 'IMPORT'} */ (initialStep));
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(/** @type {string | null} */ (null));

  /** @param {Parameters<typeof createEmailTemplate>[0]} input @param {string} busyKey */
  const create = async (input, busyKey) => {
    setBusy(busyKey);
    try {
      const template = await createEmailTemplate({ ...input, ...(name.trim() ? { name: name.trim() } : {}) });
      onCreated(template);
    } catch (error) {
      showErrorSnackbar(error);
      setBusy(null);
    }
  };

  const options = /** @type {const} */ ([
    { key: 'gallery', icon: 'IconLayoutGrid', action: () => setStep('GALLERY') },
    { key: 'blocks', icon: 'IconLayoutRows', action: () => create({ editorMode: 'BLOCKS' }, 'blocks') },
    { key: 'richText', icon: 'IconTypography', action: () => create({ editorMode: 'RICH_TEXT' }, 'richText') },
    { key: 'importHtml', icon: 'IconCode', action: () => setStep('IMPORT') },
  ]);

  const title = step === 'GALLERY' ? t('emailTemplates.gallery.title') : step === 'IMPORT' ? t('emailTemplates.create.importHtml') : t('emailTemplates.create.title');
  return html`<${EmailDialog} title=${title} onClose=${onClose} size=${step === 'GALLERY' ? 'large' : 'medium'} className="email-create-dialog" testId="email-create-dialog"
    headerActions=${step !== 'CHOOSE' && initialStep === 'CHOOSE' ? html`<${Button} title=${t('common.back')} icon="IconChevronLeft" size="small" variant="tertiary" onClick=${() => setStep('CHOOSE')} />` : null}>
    ${step === 'CHOOSE' || step === 'IMPORT'
      ? html`<div class="email-create-name">
          <label class="input-label" for="email-template-name">${t('emailTemplates.create.name')}</label>
          <input id="email-template-name" class="text-input" value=${name} maxLength=${120} placeholder=${t('emailTemplates.create.namePlaceholder')}
            onInput=${(/** @type {Event} */ event) => setName(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
        </div>`
      : null}
    ${step === 'CHOOSE'
      ? html`<div class="email-create-options">
          ${options.map((option) => html`<button type="button" class="email-create-option" disabled=${Boolean(busy)} data-busy=${busy === option.key ? 'true' : undefined}
            onClick=${option.action} data-testid=${`email-create-${option.key}`}>
            <span class="email-create-option-icon"><${Icon} name=${option.icon} size=${22} /></span>
            <span class="email-create-option-title">${t(`emailTemplates.create.${option.key}`)}</span>
            <span class="email-create-option-hint">${t(`emailTemplates.create.${option.key}Hint`)}</span>
          </button>`)}
        </div>`
      : step === 'GALLERY'
        ? html`<${EmailGalleryGrid} busyKey=${busy} onUse=${(/** @type {EmailGalleryItem} */ item) => create({ galleryKey: item.key }, item.key)} />`
        : html`<${HtmlImportPanel} onImported=${(/** @type {{ document: any }} */ result) => create({ editorMode: 'HTML', document: result.document }, 'importHtml')} />`}
  <//>`;
}
